"use client";

import {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useSession } from "next-auth/react";

const SoundContext = createContext();

// Calibration de la voix de BeBot (ElevenLabs sort à plein niveau, bien plus
// fort que les effets sonores). Appliquée à la LECTURE : le curseur de réglage
// n'est pas touché, son 100 % vaut simplement un niveau tenable.
const BEBOT_VOICE_GAIN = 0.61;

const clampVolume = (value) => {
    const v = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(v)) return 1;
    return Math.max(0, Math.min(1, v));
};

export function SoundProvider({ children }) {
    const { data: session, update } = useSession();

    // SOURCE UNIQUE : les préférences du compte, lues depuis la session (le callback
    // session() rafraîchit session.user depuis le backend). Pas de localStorage.
    // "ready" = préférences chargées. Tant que ce n'est pas le cas, on ne joue AUCUN
    // son (sinon le 1er son partait avec le défaut, avant de connaître le choix).
    const ready = session?.user?.soundEnabled !== undefined;

    // Cinq réglages persistés :
    // - masterVolume : volume général (multiplie effets ET voix)
    // - soundEnabled / soundVolume : effets sonores (on/off + volume)
    // - bebotVoiceEnabled / bebotVolume : voix de BeBot (on/off + volume)
    // Les effets sonores ont chacun leur volume "de base" pour équilibrer les
    // niveaux ; la voix ElevenLabs, elle, sortait à plein régime et écrasait
    // tout. Même principe : une calibration appliquée à la LECTURE, pas au
    // réglage — le curseur continue d'afficher ce que l'apprenant a choisi,
    // et son 100 % correspond désormais à un niveau tenable.
    const sessionMasterVolume = clampVolume(session?.user?.masterVolume ?? 1);
    const sessionEnabled = session?.user?.soundEnabled ?? true;
    const sessionVolume = clampVolume(session?.user?.soundVolume ?? 1);
    const sessionBebotEnabled = session?.user?.bebotVoiceEnabled ?? true;
    const sessionBebotVolume = clampVolume(session?.user?.bebotVolume ?? 1);

    // La valeur DÉRIVE de la session (donc toujours en phase avec "ready"). L'override
    // optimiste sert juste à la réactivité immédiate du toggle/slider, puis s'efface
    // dès que la session a rattrapé la valeur persistée.
    const [masterVolumeOverride, setMasterVolumeOverride] = useState(null);
    const [enabledOverride, setEnabledOverride] = useState(null);
    const [volumeOverride, setVolumeOverride] = useState(null);
    const [bebotEnabledOverride, setBebotEnabledOverride] = useState(null);
    const [bebotVolumeOverride, setBebotVolumeOverride] = useState(null);

    const masterVolume =
        masterVolumeOverride !== null ? masterVolumeOverride : sessionMasterVolume;
    const soundEnabled =
        enabledOverride !== null ? enabledOverride : sessionEnabled;
    const volume = volumeOverride !== null ? volumeOverride : sessionVolume;
    const bebotVoiceEnabled =
        bebotEnabledOverride !== null ? bebotEnabledOverride : sessionBebotEnabled;
    const bebotVolume =
        bebotVolumeOverride !== null ? bebotVolumeOverride : sessionBebotVolume;

    // Efface chaque override dès que la session a rattrapé la valeur persistée.
    useEffect(() => {
        if (masterVolumeOverride !== null && sessionMasterVolume === masterVolumeOverride) {
            setMasterVolumeOverride(null);
        }
    }, [sessionMasterVolume, masterVolumeOverride]);
    useEffect(() => {
        if (enabledOverride !== null && sessionEnabled === enabledOverride) {
            setEnabledOverride(null);
        }
    }, [sessionEnabled, enabledOverride]);
    useEffect(() => {
        if (volumeOverride !== null && sessionVolume === volumeOverride) {
            setVolumeOverride(null);
        }
    }, [sessionVolume, volumeOverride]);
    useEffect(() => {
        if (bebotEnabledOverride !== null && sessionBebotEnabled === bebotEnabledOverride) {
            setBebotEnabledOverride(null);
        }
    }, [sessionBebotEnabled, bebotEnabledOverride]);
    useEffect(() => {
        if (bebotVolumeOverride !== null && sessionBebotVolume === bebotVolumeOverride) {
            setBebotVolumeOverride(null);
        }
    }, [sessionBebotVolume, bebotVolumeOverride]);

    // Refs à jour pour la lecture (playSound est appelé de façon asynchrone).
    const masterVolumeRef = useRef(masterVolume);
    const enabledRef = useRef(soundEnabled);
    const volumeRef = useRef(volume);
    const bebotEnabledRef = useRef(bebotVoiceEnabled);
    const bebotVolumeRef = useRef(bebotVolume);
    const readyRef = useRef(ready);
    useEffect(() => {
        masterVolumeRef.current = masterVolume;
    }, [masterVolume]);
    useEffect(() => {
        enabledRef.current = soundEnabled;
    }, [soundEnabled]);
    useEffect(() => {
        volumeRef.current = volume;
    }, [volume]);
    useEffect(() => {
        bebotEnabledRef.current = bebotVoiceEnabled;
    }, [bebotVoiceEnabled]);
    useEffect(() => {
        bebotVolumeRef.current = bebotVolume;
    }, [bebotVolume]);
    useEffect(() => {
        readyRef.current = ready;
    }, [ready]);

    const debounceRef = useRef(null);

    // Instantané des cinq valeurs courantes (lues depuis les refs à jour).
    const snapshot = () => ({
        masterVolume: masterVolumeRef.current,
        soundEnabled: enabledRef.current,
        soundVolume: volumeRef.current,
        bebotVoiceEnabled: bebotEnabledRef.current,
        bebotVolume: bebotVolumeRef.current,
    });

    // Persistance côté compte, puis update() recharge la session (qui reste la source).
    // On envoie TOUJOURS les cinq valeurs (depuis les refs) pour ne jamais écraser
    // un réglage avec une valeur obsolète.
    const persist = useCallback(
        async (values) => {
            try {
                await fetch("/api/user/update-sound", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(values),
                });
                await update();
            } catch (e) {
                // Erreur réseau : l'override optimiste reste affiché.
            }
        },
        [update],
    );

    // Les sliders déclenchent beaucoup de changements : on débounce l'écriture.
    const persistDebounced = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => persist(snapshot()), 400);
    }, [persist]);

    const setSoundEnabled = useCallback(
        (value) => {
            const next =
                typeof value === "function" ? value(enabledRef.current) : value;
            enabledRef.current = next;
            setEnabledOverride(next);
            persist(snapshot());
        },
        [persist],
    );

    const setBebotVoiceEnabled = useCallback(
        (value) => {
            const next =
                typeof value === "function"
                    ? value(bebotEnabledRef.current)
                    : value;
            bebotEnabledRef.current = next;
            setBebotEnabledOverride(next);
            persist(snapshot());
        },
        [persist],
    );

    const setMasterVolume = useCallback(
        (value) => {
            const next = clampVolume(
                typeof value === "function"
                    ? value(masterVolumeRef.current)
                    : value,
            );
            masterVolumeRef.current = next;
            setMasterVolumeOverride(next);
            persistDebounced();
        },
        [persistDebounced],
    );

    const setVolume = useCallback(
        (value) => {
            const next = clampVolume(
                typeof value === "function" ? value(volumeRef.current) : value,
            );
            volumeRef.current = next;
            setVolumeOverride(next);
            persistDebounced();
        },
        [persistDebounced],
    );

    const setBebotVolume = useCallback(
        (value) => {
            const next = clampVolume(
                typeof value === "function"
                    ? value(bebotVolumeRef.current)
                    : value,
            );
            bebotVolumeRef.current = next;
            setBebotVolumeOverride(next);
            persistDebounced();
        },
        [persistDebounced],
    );

    const toggleSound = useCallback(() => {
        setSoundEnabled((prev) => !prev);
    }, [setSoundEnabled]);

    // La promesse dit si le son a été joué : `false` quand le navigateur l'a
    // refusé (onglet où l'on n'a encore rien cliqué), rien quand il est coupé
    // dans les réglages.
    const playSound = (audioPath, options = {}) => {
        // Jamais de son tant que les préférences ne sont pas chargées, ni si coupé.
        if (!readyRef.current || !enabledRef.current) return Promise.resolve();

        try {
            const audio = new Audio(audioPath);
            // Effets : volume de base × volume des effets × volume général
            audio.volume = clampVolume(
                (options.volume ?? 0.5) *
                    volumeRef.current *
                    masterVolumeRef.current,
            );

            if (options.playbackRate) {
                audio.playbackRate = options.playbackRate;
            }

            audio.load();
            const playPromise = audio.play();

            if (playPromise !== undefined) {
                return playPromise
                    .then(() => true)
                    .catch((error) => {
                        console.warn(`Audio play failed for ${audioPath}:`, error);
                        return false;
                    });
            }

            return Promise.resolve(true);
        } catch (error) {
            console.warn(`Audio creation failed for ${audioPath}:`, error);
            return Promise.reject(error);
        }
    };

    return (
        <SoundContext.Provider
            value={{
                ready,
                // Volume général
                masterVolume,
                setMasterVolume,
                // Effets sonores
                soundEnabled,
                setSoundEnabled,
                toggleSound,
                volume,
                setVolume,
                // Voix de BeBot
                bebotVoiceEnabled,
                setBebotVoiceEnabled,
                bebotVolume,
                setBebotVolume,
                // Volume RÉEL de lecture de la voix : réglage de l'apprenant ×
                // volume général × calibration. À utiliser partout où on joue
                // la voix (le réglage brut, lui, reste celui du curseur).
                bebotPlaybackVolume: clampVolume(
                    bebotVolume * masterVolume * BEBOT_VOICE_GAIN
                ),
                playSound,
            }}
        >
            {children}
        </SoundContext.Provider>
    );
}

export function useSound() {
    const context = useContext(SoundContext);
    if (!context) {
        throw new Error("useSound must be used within a SoundProvider");
    }
    return context;
}
